import { capMatchPercent, formatFitScoreBadge } from "./matchScoreUtils";
import { formatSocialProofLine } from "./socialProofUtils";

export const MATCH_LABEL_STATES = {
  ALIGNED: "aligned",
  RELATED: "related",
  EXPLORE: "explore",
};

function normalizeCategory(value) {
  if (!value || typeof value !== "string") return "";
  return value.trim().toLowerCase();
}

export function getProductCategoryName(product) {
  const category = product?.category;
  if (!category) return null;
  if (typeof category === "object") {
    return category.name || null;
  }
  return null;
}

function getProductId(product) {
  const id = product?._id || product?.id;
  return id != null ? String(id) : null;
}

function getActiveSearchLabel(activeDecision) {
  if (!activeDecision?.hasActiveDecision) return null;
  if (activeDecision.useCase) return activeDecision.useCase;
  if (activeDecision.category) return activeDecision.category;
  return "your current search";
}

function buildRelatedTooltip(productCategory, activeDecision) {
  const currentSearch = getActiveSearchLabel(activeDecision);
  const preferenceLine = `Based on your ${productCategory} preferences.`;
  if (!currentSearch) return preferenceLine;
  return `${preferenceLine} Your current search: ${currentSearch}.`;
}

function buildAriaLabel(score, context, activeDecision) {
  const pct = capMatchPercent(score);
  const state = context?.state || MATCH_LABEL_STATES.ALIGNED;
  if (state === MATCH_LABEL_STATES.ALIGNED) {
    return `Fit score ${pct} out of 100`;
  }
  if (state === MATCH_LABEL_STATES.RELATED && context?.qualifier) {
    const current = activeDecision?.category || getActiveSearchLabel(activeDecision);
    if (current) {
      return `Fit score ${pct} out of 100 for ${context?.qualifier}, not your current ${current} search`;
    }
    return `Fit score ${pct} out of 100 for ${context?.qualifier}`;
  }
  return `Fit score ${pct} out of 100`;
}

function categoryRefIsUnpopulated(category) {
  if (!category) return false;
  if (typeof category === "string") return true;
  if (typeof category === "object" && category._id && !category.name) return true;
  return false;
}

/**
 * Decide how to label a match score on feed posts.
 * @param {object|null|undefined} product
 * @param {object|null|undefined} activeDecision
 * @param {{ shopperCategoryKeys?: string[] }} [options]
 */
export function getMatchLabelContext(product, activeDecision, options = {}) {
  const productCategory = getProductCategoryName(product);
  const productId = getProductId(product);
  const decisionCategory = activeDecision?.category || null;
  const hasActiveDecision = Boolean(activeDecision?.hasActiveDecision);
  const shopperCategoryKeys = options.shopperCategoryKeys || [];

  if (hasActiveDecision && categoryRefIsUnpopulated(product?.category)) {
    return {
      state: MATCH_LABEL_STATES.ALIGNED,
      qualifier: null,
      tooltip: null,
    };
  }

  const onShortlist =
    productId &&
    Array.isArray(activeDecision?.productIds) &&
    activeDecision.productIds.some((id) => String(id) === productId);

  const categoryMatchesDecision =
    hasActiveDecision &&
    productCategory &&
    decisionCategory &&
    normalizeCategory(productCategory) === normalizeCategory(decisionCategory);

  if (onShortlist || categoryMatchesDecision) {
    return {
      state: MATCH_LABEL_STATES.ALIGNED,
      qualifier: null,
      tooltip: null,
    };
  }

  if (!hasActiveDecision) {
    return {
      state: MATCH_LABEL_STATES.ALIGNED,
      qualifier: null,
      tooltip: null,
    };
  }

  const hasCategoryProfile =
    productCategory &&
    (shopperCategoryKeys.length === 0 ||
      shopperCategoryKeys.some(
        (key) => normalizeCategory(key) === normalizeCategory(productCategory),
      ));

  if (productCategory && hasCategoryProfile) {
    return {
      state: MATCH_LABEL_STATES.RELATED,
      qualifier: productCategory,
      tooltip: buildRelatedTooltip(productCategory, activeDecision),
    };
  }

  return {
    state: MATCH_LABEL_STATES.EXPLORE,
    qualifier: null,
    tooltip: null,
  };
}

/**
 * Format score line with optional category qualifier and social proof.
 */
export function formatContextualMatchLine(score, context, proof) {
  if (score == null || !Number.isFinite(Number(score))) {
    return formatSocialProofLine(proof);
  }

  const pct = capMatchPercent(score);
  const proofPart = formatSocialProofLine(proof);

  let scorePart;
  if (context?.state === MATCH_LABEL_STATES.RELATED && context.qualifier) {
    scorePart = `Fit score ${pct}/100 for ${context.qualifier}`;
  } else {
    scorePart = `Fit score ${pct}/100`;
  }

  if (!proofPart) return scorePart;
  return `${scorePart} · ${proofPart}`;
}

/**
 * Short inline label for feed cards — full context belongs in the tooltip.
 */
export function formatCompactMatchLine(score, context, proof) {
  if (score == null || !Number.isFinite(Number(score))) {
    return formatSocialProofLine(proof);
  }

  return formatFitScoreBadge(score);
}

/**
 * Tooltip text when the inline match label is compact.
 */
export function buildCompactMatchTooltip(score, context, proof, activeDecision) {
  if (score == null || !Number.isFinite(Number(score))) {
    return formatSocialProofLine(proof) || undefined;
  }

  const compact = formatCompactMatchLine(score, context, proof);
  const full = formatContextualMatchLine(score, context, proof);

  if (full && full !== compact) {
    return full;
  }

  if (context?.tooltip) {
    return context.tooltip;
  }

  const aria = buildMatchLabelAriaLabel(score, context, activeDecision);
  if (aria && aria !== compact) {
    return aria;
  }

  return undefined;
}

export function buildMatchLabelAriaLabel(score, context, activeDecision) {
  if (score == null || !Number.isFinite(Number(score))) return undefined;
  return buildAriaLabel(score, context, activeDecision);
}
